import { useState, useEffect, useRef, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

interface UnreadCountResponse {
  count: number;
}

/**
 * Fetch unread inbox conversation count for sidebar badge.
 *
 * Polls unread count via api-crm gateway. Refreshes every 30s.
 */
const POLL_INTERVAL_MS = 30_000;

export function useUnreadCount() {
  const [data, setData] = useState<UnreadCountResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchCount = useCallback(() => {
    const promise = apiFetch<{ data: UnreadCountResponse }>("/email/inbox/unread-count");

    promise
      .then((res) => setData(res.data))
      .catch(() => setData({ count: 0 }))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchCount();
    intervalRef.current = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchCount]);

  return { unreadCount: data?.count ?? 0, isLoading };
}
